import { useState, useEffect, useCallback, useRef, memo, useMemo } from "react";
import { lazy, Suspense } from "react";
import { useEffectOnce } from "react-use";
import styles from "./ChatPage.module.scss";
import { Navigate, Route, Routes } from "react-router-dom";

import classNames from "classnames";

import { MainView } from "views/layout/MainView";
import { FormattedMessage } from "react-intl";

import useRouter from "hooks/useRouter";
import { useAppNotification } from "hooks/services/AppNotification";
import { useWindowSize } from "hooks/useWindowSize";
import { useMiniChatInfo } from "hooks/services/ThreadServices/useMiniChatInfo";
import { useToggleSidebar } from "hooks/services/ToggleSidebarProvider";
import { useThreadsHistoryContext } from "hooks/ThreadHistoryContext";
import { ScrollToBottomButton } from "pages/ChatPage/components/chatFooter/components/ScrollToBottomButton";
import {
  startNewChat,
  getChatHistory,
  getActivePlan,
  IMessage,
  setChatModel,
  EChatType,
  ChatType,
  IChat,
  getChatSettings,
  SettingsBase,
  removeMultipleChat,
  setDropDownOptions,
  setRememberSetting,
  RoleBot,
  IChatHistoryResponse,
  setMessagesPagination,
  clearhistoryMessages,
  filterLanguages,
  setCreditLoad,
  setIsPlanExpired,
  setSelectedSubOptions,
  setIsEditing,
  SetCompatibilityModal,
  SetInCompatibilityModal,
  setRememberChatTopic,
  EmptyThreadChat,
  EmptyChatContent,
  EmptyUploadedFiles,
  // setMsgStartGenerating,
  // setCreditLimitModal,
} from "redux/actions";
import { useChatStream } from "hooks/services/ChatServices";
import { useSelector } from "redux/hooks";
import { RoutePaths } from "pages/routePaths";
import { ShareChat } from "./pages/ShareChatPage";
import { ChatSettingModal } from "./components/chatSettingModal";
import { FileSizeLimitModal } from "./components/fileSizeLimitModal";
import { EmptyObj } from "utils/fileProcessing";
import { getDropdownOptions } from "utils/constants";
import useChat from "hooks/services/ChatServices/useChat";
import { CompatibleModal } from "components/CompatibleModal/CompatibleModal";
import { InCompatibleModal } from "components/InCompatibleFileModal/InCompatibleModal";
import { WorkspaceSearchField } from "pages/Workspaces/pages/WorkspaceConversation.tsx/InfoComponent/WorkspaceSearchField";
import { InfoChat } from "pages/Workspaces/pages/WorkspaceConversation.tsx/InfoComponent/InfoChat";
import { UploadedFilePreview } from "pages/Workspaces/pages/WorkspaceConversation.tsx/Threads/Components/UploadedFilePreview";

import { useFileUpload } from "hooks/useFileUpload";
import { DragDropModal } from "pages/Workspaces/pages/WorkspaceConversation.tsx/Component/Drag&DropModal";
import { ErrorHandler } from "pages/Workspaces/components/ErrorHandler/ErrorHandler";
import { ContinueIcon } from "pages/Workspaces/components/icons/ContinueIcon";
import { MiniTConversationHeader } from "pages/Workspaces/pages/ThreadsConversation/MiniTConversationHeader";
// import { useSidebar } from "hooks/services/ReSizeSidebar";
import { loadMoreMessages } from "utils/chat";
import { isIOS, isSafari } from "react-device-detect";

const NewChat = lazy(() =>
  import("./pages").then((module) => ({ default: module.NewChat }))
);

const ChatHistory = lazy(() =>
  import("./pages").then((module) => ({ default: module.ChatHistory }))
);

// interfaces
interface IRoute {
  path?: string;
  component: React.ReactNode;
}

export const ChatRoute = {
  New: "new",
  History: "history",
} as const;

export interface IGetMessageAgainstKey {
  messagesArray: IMessage[];
  key: any;
}

export interface IUploadFile {
  id: string;
  file: File;
  status: "validating" | "uploading" | "uploaded" | "error";
  fileType: "document" | "image" | "video" | "audio";
  S3Link?: string;
  customFileName?: string;
}

export interface SelectedSubOptions {
  [key: string]: string;
}

export const ChatPage = memo(() => {
  // custom hooks
  const { controllerRef } = useChatStream();

  const {
    isAllChunksReceived,
    isGenerating,
    IGLoading,
    onSendMessage,
    onRegenerate,
    sizeMessage,
    setIsAnswerComplete,
    isAnswerComplete,
    setIsGenerating,
    onChangeChat,
    sendMessageLoading,
    getMessagesLoading,
    showScrollToBottom,
    scrollToBottom,
    setGetMessagesLoading,
    chatContainerRef,
  } = useThreadsHistoryContext();

  const {
    selectedMessages,
    messageHeight,
    setMessageHeight,
    setSelectedMessages,
    setSelectedChatId,
  } = useChat();

  const { pathname, push, includeRoute } = useRouter();
  const chatId = pathname.split("/")[3];
  const { triggerNotification } = useAppNotification();

  // data from reducer
  const chatModel = useSelector((state) => state.authReducer.gptModel);
  const chatHistory = useSelector((state) => state.chatReducer.chats);
  const { chatModels } = useSelector((state) => state.chatModelsReducer);
  const {
    newMessages,
    messages,
    chatSetting,
    PrivateChat,
    AdminDelErrorMsg,
    compatibleModel,
    inCompatibleModel,
    FileContent,
    messagesPagination,
    isLoadMoreMessages,
    // creditLimitModal,
  } = useSelector((state) => state.chatReducer);
  const { userDetail, gptModel, planExpired } = useSelector(
    (state) => state.authReducer
  );
  const { isModalOpen, openUploadURLModel } = useSelector(
    (state) => state.workSpaceReducer
  );
  const { errorModal, setErrorModal, messageId, setMessageId, handleDrop } =
    useFileUpload(false);
  // target DOM using ref
  const contentContainerRef = useRef<HTMLDivElement | null>(null);


  // state management using useState hook
  // const [creditLimitModal, setCreditLimitModal] = useState<boolean>(false);
  // const toggleCreditLimitModal = () =>
  //   setCreditLimitModal(!creditLimitModal);

  // setCreditLimitModal((prev) => {
  //   if (prev) {
  //     return false;
  //   } else {
  //     ReactGA.event({
  //       action: "Credits_LimitReached_NotificationShown",
  //       category: "CreditCompletedPopupShown",
  //       label: "CreditCompletedPopupShown",
  //     });
  //     return true;
  //   }
  // });
  // const onConfirmCreditModal = () =>
  //   push(`/${RoutePaths.Settings}/${RoutePaths.CurrentPlan}`);
  const { width } = useWindowSize();
  const { showMiniChatInfo,setShowMiniChatInfo } = useMiniChatInfo();
  const { isOpen } = useToggleSidebar();
  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
  const [drag, setIsDrag] = useState<boolean>(false);
  const [isMainScreenOpen, setIsMainScreenOpen] = useState<boolean>(true);
  const [loadingShareChat, setLoadingShareChat] = useState(false);
  const [chatItem, setChatItem] = useState<IChat>();
  const [uploadingFiles, setUploadingFiles] = useState<IUploadFile[]>([]);
  const [fileS3Link, setFileS3Link] = useState<string[]>([]);
  // const [messageId, setMessageId] = useState<string>("");
  const [changeModel, setChangeModel] = useState<boolean>(false);
  const [showInfo, setShowInfo] = useState(false);
  // const { sidebarWidth } = useSidebar();
  // Abort Generate Related Question
  const abortControllerRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // STATE FOR SHARING CHAT
  const [share, setShare] = useState<boolean>(false);
  const toggleShareChat = () => setShare((prev) => !prev);

  // SEARCH CHAT HISTORY STATE
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchQueryRef = useRef(searchQuery);

  // State for chat Seeting
  const [chatSettings, setChatSetting] = useState<boolean>(false);
  const [loadingSetting, setLoadingSetting] = useState<boolean>(false);
  const [settings, setSettings] = useState({
    real_time_results: chatSetting.real_time_results,
    related_questions: chatSetting.related_questions,
    send_message_with_enter: chatSetting.send_message_with_enter,
  });

  const isChat = includeRoute(RoutePaths.Chat);

  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  useEffect(() => {
    if (newMessages.length > 0) setIsMainScreenOpen(false);
  }, [newMessages]);

  useEffect(
    () => () => {
      if (compatibleModel) {
        startNewChat();
        EmptyChatContent();
        setRememberChatTopic("");
      }
    },
    []
  );

  useEffect(()=>{
    if(showInfo){
        setShowMiniChatInfo(false)
    }
  
  },[showInfo])

  // Hide chat info by default
  useEffect(()=>{
      setShowMiniChatInfo(true)
  },[])

  const clearPrivateChat = (chatIds: number[]) => {
    removeMultipleChat(chatIds);
    sessionStorage.removeItem("PrivateChatId");
  };

  const onGetChatHistory = useCallback(() => {
    EmptyThreadChat(EmptyObj);
    setIsEditing(false);
    const history = pathname.includes("/chat/history");
    const previousPrivateChat = JSON.parse(
      localStorage.getItem("previousPrivateChat") || "false"
    );
    const historyPrivateChatId = Number(
      localStorage.getItem("PrivatehistoryChatID")
    );
    const PrivateChatId = Number(sessionStorage.getItem("PrivateChatId"));
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split("/");
    const chatIdFromUrl = pathSegments[pathSegments.length - 2];

    if (previousPrivateChat && PrivateChatId && !history) {
      clearPrivateChat([PrivateChatId]);
      return;
    }

    if (
      previousPrivateChat &&
      (PrivateChatId || historyPrivateChatId) === Number(chatIdFromUrl) &&
      !PrivateChat &&
      history
    ) {
      if (PrivateChatId === Number(chatIdFromUrl))
        removeMultipleChat([PrivateChatId]);
      push(`/${RoutePaths.Chat}/${ChatRoute.New}`);
      localStorage.removeItem("previousPrivateChat");
      sessionStorage.removeItem("PrivateChatId");
      localStorage.removeItem("historyPrivateChatId");
      return;
    }

    if (chatId) {
      setTimeout(() => {
        setGetMessagesLoading(true);
      }, 0);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController();

      clearhistoryMessages();
      EmptyChatContent();
      setRememberChatTopic("");
      EmptyUploadedFiles();
      // Abort the previous request if it exists

      if (previousPrivateChat && PrivateChatId)
        clearPrivateChat([PrivateChatId]);
      setMessagesPagination(1, 1);

      getChatHistory(JSON.parse(chatId), {
        signal: abortControllerRef.current.signal,
      })
        .then((resp) => {
          const response = resp as IChatHistoryResponse;
          const { chat } = response;
          if (chat?.topic) {
            setRememberChatTopic(chat.topic);
          }

          if (chat?.id) setChatItem(chat);
          requestAnimationFrame(() => {
            setTimeout(() => {
              scrollToBottom({ behavior: "auto" });
            }, 3);
            setGetMessagesLoading(false);
          });
        })
        .catch((err: any) => {
          setGetMessagesLoading(false);
          if (err.name !== "AbortError") {
            triggerNotification({ message: err?.data?.message, type: "error" });
            if (err?.data?.message === "Chat not found")
              push(`/${RoutePaths.Chat}/${ChatRoute.New}`);
          }
        });
    }
  }, [chatId, triggerNotification, setGetMessagesLoading]);

  useEffect(() => {
    if (chatId) {
      let chatItem = chatHistory.find((chat) => chat.id === Number(chatId));
      if (chatItem && chatModels?.length > 0) {
        if (!chatModel?.type?.includes(chatItem?.chat_type as EChatType)) {
          setChatModel(
            chatModels?.filter((item) =>
              item?.type?.includes(chatItem?.chat_type as EChatType)
            )?.[0]
          );
        }
      }
    }
  }, [chatId, chatHistory, chatModel, chatModels]);

  useEffect(() => onGetChatHistory(), [onGetChatHistory]);

  useEffectOnce(() => {
    setIsEditing(false);
    localStorage.removeItem("invite");
    if (userDetail?.token) {
      setCreditLoad(true);
      setLoadingSetting(true);

      getChatSettings()
        .then((res) => {
          setLoadingSetting(false);
          const chatSettings = res as SettingsBase;

          const options = getDropdownOptions(
            chatSettings?.bot_settings_allowed
          );
          setDropDownOptions(options);
          filterLanguages(chatModel?.model ?? "");
          setRememberSetting(
            chatSettings?.chat_settings?.customized_response || false
          );

          setSettings({
            real_time_results:
              chatSettings?.chat_settings?.real_time_results || false,
            related_questions:
              chatSettings?.chat_settings?.related_questions || false,
            send_message_with_enter:
              chatSettings?.chat_settings?.send_message_with_enter || false,
          });
          if (chatSettings?.chat_settings?.customized_response === true) {
            setSelectedSubOptions({
              outputFormats:
                chatSettings?.bot_settings?.output_format || "default",
              tones: chatSettings?.bot_settings?.tone || "default",
              writingStyles:
                chatSettings?.bot_settings?.style_of_writing || "default",
              responseLengths: chatSettings?.bot_settings?.length || "default",
              language: chatSettings?.bot_settings?.language || "default",
            });
          }
          if (chatSettings?.chat_settings?.customized_response === false) {
            const updatedBot = {
              key: "bot_settings",
            };
            setSelectedSubOptions({
              outputFormats: "default",
              tones: "default",
              writingStyles: "default",
              responseLengths: "default",
              language: "default",
            });
            RoleBot(updatedBot);
          }
        })
        .catch((err) => {
          setLoadingSetting(false);
          triggerNotification({ message: err?.data?.message, type: "error" });
        });

      getActivePlan()
        .then(() => {
          setCreditLoad(false);
        })
        .catch((err) => {
          setCreditLoad(false);
          triggerNotification({ message: err?.data?.message, type: "error" });
        });
    }
  });

  // Use useMemo to cache the result of isDisabled calculation
  const isAllowUploadFile = useMemo(() => {
    if (
      gptModel?.type.includes(ChatType.image) &&
      !gptModel?.type?.includes("image_chat")
    ) {
      return false;
    }
    if (openUploadURLModel) {
      return false;
    }
    if (isModalOpen) return true;
    return true;
  }, [gptModel, newMessages.length, messages]);

  const onConfirm = () => {
    setIsPlanExpired(false);
    push(`/${RoutePaths.Settings}/${RoutePaths.CurrentPlan}`);
  };

  const onCancel = () => {
    setMessageId("");
    setIsPlanExpired(false);
  };

  useEffect(() => {
    const handleUpdate = (e: any) => {
      const settings = e.detail;
      if (settings?.key === "bot_settings") {
        setSelectedSubOptions(settings);
      }
      if (settings?.key === "chat_settings") {
        setSettings(settings);
        setRememberSetting(settings?.customized_response);
      }
    };
    document.addEventListener("updatedSettings", handleUpdate);
    return () => {
      document.removeEventListener("updatedSettings", handleUpdate);
    };
  }, []);

  let userScrolled = false;

  const handleMessageScroll = (
    event: React.UIEvent<HTMLDivElement, UIEvent>
  ) => {
    if (messagesPagination.page < messagesPagination.lastPage) {
      const { scrollTop } = event?.currentTarget;
      const scrollPositionFromTop = scrollTop;
      const loadThreshold = 1;

      if (scrollPositionFromTop <= loadThreshold && !isLoadMoreMessages) {
        loadMoreMessages(chatId, messagesPagination, chatContainerRef);
      }
    }
  };

    const handleScroll = () => {
      if (
        isIOS &&
        isSafari &&
        textareaRef &&
        textareaRef.current &&
        userScrolled &&
        width <= 576
      ) {
        textareaRef.current.blur();
        userScrolled = false;
      }
    };

  const routes: IRoute[] = useMemo(() => {
    return [
      {
        path: `${ChatRoute.New}`,
        component: (
          <Suspense fallback={<div />}>
            <NewChat
              isMainScreenOpen={isMainScreenOpen}
              isAllChunksReceived={isAllChunksReceived}
              newMessages={newMessages}
              isGenerating={isGenerating}
              isFileUploading={isFileUploading}
              IGLoading={IGLoading}
              setIsAnswerComplete={setIsAnswerComplete}
              setIsGenerating={setIsGenerating}
              onSendMessage={onSendMessage}
              onRegenerate={onRegenerate}
              selectedMessages={selectedMessages}
              setSelectedMessages={setSelectedMessages}
              share={share}
              toggleShareChat={toggleShareChat}
              setSelectedChatId={setSelectedChatId}
              messageHeight={messageHeight}
              setMessageHeight={setMessageHeight}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              drag={drag}
              setIsMainScreenOpen={setIsMainScreenOpen}
              setIsDrag={setIsDrag}
              setUploadingFiles={setUploadingFiles}
              setFileS3Link={setFileS3Link}
              setIsFileUploading={setIsFileUploading}
              uploadingFiles={uploadingFiles}
              changeModel={changeModel}
              setChangeModel={setChangeModel}
            />
          </Suspense>
        ),
      },
      {
        path: `${ChatRoute.History}/:chatId/:folder_id?`,
        component: (
          <Suspense fallback={<div />}>
            <ChatHistory
              isAllChunksReceived={isAllChunksReceived}
              isloading={getMessagesLoading}
              messages={messages}
              isGenerating={isGenerating}
              setIsAnswerComplete={setIsAnswerComplete}
              setIsGenerating={setIsGenerating}
              onSendMessage={onSendMessage}
              IGLoading={IGLoading}
              onRegenerate={onRegenerate}
              selectedMessages={selectedMessages}
              setSelectedMessages={setSelectedMessages}
              share={share}
              toggleShareChat={toggleShareChat}
              setSelectedChatId={setSelectedChatId}
              messageHeight={messageHeight}
              setMessageHeight={setMessageHeight}
              chatItem={chatItem}
              searchQuery={searchQuery}
            />
          </Suspense>
        ),
      },
    ];
  }, [
    isAllChunksReceived,
    newMessages,
    messages,
    getMessagesLoading,
    isGenerating,
    isFileUploading,
    IGLoading,
    isMainScreenOpen,
    onRegenerate,
    onSendMessage,
    setIsGenerating,
    setIsAnswerComplete,
    setIsFileUploading,
    setIsMainScreenOpen,
  ]);

  return (
    <MainView
      sendMessageLoading={sendMessageLoading}
      isAnswerComplete={isAnswerComplete}
      onChangeChat={onChangeChat}
      contentContainerRef={contentContainerRef}
      isMainScreenOpen={isMainScreenOpen}
      setIsMainScreenOpen={setIsMainScreenOpen}
      setShare={setShare}
      setSelectedMessages={setSelectedMessages}
      setChatItem={setChatItem}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      setChatSetting={setChatSetting}
      setIsDrag={setIsDrag}
      isFileUploading={isFileUploading}
      controllerRef={controllerRef}
      uploadingFiles={uploadingFiles}
      setUploadingFiles={setUploadingFiles}
      setFileS3Link={setFileS3Link}
      isAllowUploadFile={isAllowUploadFile}
      fileS3Link={fileS3Link}
      textareaRef={textareaRef}
      userScrolled={userScrolled}
    >
      <>
        {(width <= 576 ||
          (width > 576 &&
            width <= 1024 &&
            ((isOpen && !showMiniChatInfo) ||
              (!isOpen && !showMiniChatInfo) ||
              (isOpen && showMiniChatInfo)))) &&
          !showInfo &&
          FileContent.length > 0 && (
            <MiniTConversationHeader
              setShowInfo={setShowInfo}
              chatPage={true}
            />
          )}
        {(width <= 576 ||
          (width > 576 &&
            width <= 1024 &&
            ((isOpen && !showMiniChatInfo) ||
              (!isOpen && !showMiniChatInfo) ||
              (isOpen && showMiniChatInfo)))) &&
        showInfo &&
        FileContent.length > 0 ? (
          <div className="py-[14px] pl-[15px] pr-[13px] flex flex-col gap-3">
            <div className="text-[#4338CA] text-[14px] font-inter">
              <div
                className="inline-flex gap-2 items-center cursor-pointer font-medium"
                onClick={() => setShowInfo(false)}
              >
                <span className="rotate-180 flex flex-row items-center justify-center">
                  <ContinueIcon />
                </span>
                <span className="cursor-pointer hover:underline">
                  <FormattedMessage id="workspace.create.back" />
                </span>
              </div>
            </div>
            <InfoChat chatPage={true} />
          </div>
        ) : (
          <div
            className={classNames(
              "flex flex-row mx-auto w-full h-[100%] gap-[30px] justify-center",
              // {
              //   "justify-center": PrivateChat || FileContent.length === 0,
              // }
            )}
          >
            <div
            className={classNames("flex flex-col gap-[30px] max-w-5xl w-full")}
              // className={`flex flex-col gap-[30px] " ${width <= 576 || (width > 576 && width <= 1024 && ((isOpen && !showMiniChatInfo) || (!isOpen && !showMiniChatInfo) || (isOpen && showMiniChatInfo))) ? "w-full" : showMiniChatInfo ? "flex-1 min-w-0" : "w-[64%]"} ${
              //   (sidebarWidth > 287 && width <= 1150) ||
              //   (sidebarWidth > 497 && width <= 1323) ||
              //   (sidebarWidth > 480 && width <= 1305)
              //     ? styles.HomeConversationContainer
              //     : ""
              // }`}
            >
              <div
                className={styles.chathistory}
                ref={chatContainerRef}
                onScroll={(e) => {
                  if (isChat) handleMessageScroll(e);
                   handleScroll();
                }}
              >
                {!window.location.pathname.includes("share-chat") && (
                  <Routes>
                    {routes.map(({ path, component: Component }) => (
                      <Route key={path} path={path} element={Component} />
                    ))}
                    <Route
                      path="*"
                      element={<Navigate to={ChatRoute.New} replace />}
                    />
                  </Routes>
                )}
              </div>
              <div className={styles.footer}>
                {!getMessagesLoading && showScrollToBottom && (
                  <ScrollToBottomButton
                    dataTestId="scroll-to-bottom"
                    onClick={() => scrollToBottom?.({ behavior: "smooth" })}
                  />
                )}
                {!isModalOpen && <UploadedFilePreview />}
                <WorkspaceSearchField chatPage={true} threads={true} />
              </div>
            </div>
            {!PrivateChat &&
              FileContent.length > 0 &&
              (width > 1024 ||
                (width > 576 &&
                  width <= 1024 &&
                  !isOpen &&
                  showMiniChatInfo)) && (
                <div
                // className="w-[34%]"
                 className={`${showMiniChatInfo ? "flex-1 min-w-0 max-w-[6rem]" : "w-full min-w-[400px] flex-1 mr-5 mt-[27px] max-w-md"}`}
                >
                  <InfoChat chatPage={true} />
                </div>
              )}
          </div>
        )}
        {window.location.pathname.includes("share-chat") && (
          <ShareChat
            loadingShareChat={loadingShareChat}
            setLoadingShareChat={setLoadingShareChat}
          />
        )}
      </>

      {/* {creditLimitModal && (
        <CreditLimitModal
          onCancel={toggleCreditLimitModal}
          onClose={toggleCreditLimitModal}
          onConfirm={onConfirmCreditModal}
        />
      )}  */}
      {chatSettings && (
        <ChatSettingModal
          onClose={() => setChatSetting(false)}
          loadingSetting={loadingSetting}
          settings={settings}
          setSettings={setSettings}
        />
      )}
      {(errorModal.show || AdminDelErrorMsg || messageId) && (
        <ErrorHandler
          errorModal={errorModal}
          setErrorModal={setErrorModal}
          messageId={messageId ? messageId : sizeMessage}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      )}

      {drag && (
        <DragDropModal
          handleClose={() => setIsDrag(false)}
          handleDrop={async (event: React.DragEvent<HTMLDivElement>) => {
            await handleDrop(event, openUploadURLModel ? false : isModalOpen);
          }}
        />
      )}

      {(messageId || planExpired) && (
        <FileSizeLimitModal
          messageId={messageId}
          onCancel={onCancel}
          onClose={onCancel}
          onConfirm={onConfirm}
        />
      )}

      {compatibleModel && (
        <CompatibleModal
          openCompatibleModel={compatibleModel}
          onClose={() => SetCompatibilityModal(false)}
          onSendMessage={onSendMessage}
          uploadingFiles={uploadingFiles}
          fileS3Link={fileS3Link}
        />
      )}

      {inCompatibleModel && (
        <InCompatibleModal
          openInCompatibleModel={true}
          onClose={() => SetInCompatibilityModal(false)}
        />
      )}
    </MainView>
  );
});
